import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, fmtDateTime, naira } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select, Pill } from "../hr/ui";
import { Sev, Spinner, Tabs } from "./mui";
import { Modal } from "./TasksChecklists";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Maintenance() {
  const { locationId, locations } = useOutletContext();
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [form, setForm] = useState(null);
  const [resolve, setResolve] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    const p = { location_id: locationId || undefined };
    api.get("/management/maintenance", { params: p }).then(setRecords).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/maintenance-schedules", { params: p }).then(setSchedules).catch(() => {});
    api.get("/management/renewals").then(setRenewals).catch(() => {});
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const { kind, ...body } = form;
      if (kind === "record") {
        if (!body.equipment) return toast.error("Equipment is required");
        await api.post("/management/maintenance", body);
      } else if (kind === "schedule") {
        if (!body.equipment) return toast.error("Equipment is required");
        await api.post("/management/maintenance-schedules", {
          ...body, recurrence: { type: body.recurrence_type || "daily", every: Number(body.every || 90) } });
      } else {
        if (!body.service_name || !body.renewal_date) return toast.error("Service name and renewal date are required");
        await api.post("/management/renewals", body);
      }
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doResolve = async () => {
    if (!notes.trim()) return toast.error("Resolution notes are required");
    try {
      await api.put(`/management/maintenance/${resolve.id}`, { status: "resolved", resolution_notes: notes });
      toast.success("Maintenance closed"); setResolve(null); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!records) return <Spinner />;

  const newBtn = {
    records: ["Log Issue", { kind: "record", equipment: "", category: "General", location_id: "", issue: "", priority: "medium" }],
    schedules: ["New Schedule", { kind: "schedule", equipment: "", category: "General", location_id: "", recurrence_type: "daily", every: 90, next_due_at: "", reminder_lead_days: 14 }],
    renewals: ["New Renewal", { kind: "renewal", service_name: "", vendor: "", location_id: "", renewal_date: "", amount: 0, reminder_lead_days: 14, notes: "" }],
  }[tab];

  return (
    <div className="space-y-5" data-testid="mgmt-maintenance-page">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs testid="maintenance-tabs" value={tab} onChange={setTab}
          tabs={[["records", "Issue Log"], ["schedules", "Service Schedules"], ["renewals", "Renewals"]]} />
        <div className="flex-1" />
        <Btn variant="primary" onClick={() => setForm({ ...newBtn[1] })} data-testid="maintenance-new-btn"><Plus size={15} /> {newBtn[0]}</Btn>
      </div>

      {tab === "records" && (
        <Card testid="maintenance-records-card">
          <SectionHead title="Maintenance Issue Log" sub={`${records.length} record(s)`} />
          <Table testid="maintenance-records-table" columns={[
            { key: "reference", label: "Ref" },
            { key: "equipment", label: "Equipment" },
            { key: "category", label: "Category" },
            { key: "location_name", label: "Location" },
            { key: "issue", label: "Issue" },
            { key: "priority", label: "Priority", render: (r) => <Sev value={r.priority} /> },
            { key: "created_at", label: "Logged", render: (r) => fmtDateTime(r.created_at) },
            { key: "status", label: "Status", render: (r) => <Pill status={r.status === "open" ? "pending" : r.status === "resolved" ? "completed" : "in_progress"} text={r.status} /> },
            { key: "act", label: "", align: "right", render: (r) => r.status !== "resolved"
              ? <Btn variant="primary" onClick={() => setResolve(r)} data-testid={`maintenance-resolve-${r.id}`}>Resolve</Btn>
              : <span className="text-[11px]" style={{ color: HC.soft }}>{r.resolution_notes}</span> },
          ]} rows={records} empty="No maintenance issues logged" />
        </Card>
      )}

      {tab === "schedules" && (
        <Card testid="maintenance-schedules-card">
          <SectionHead title="Preventive Service Schedules" sub="Recurring servicing with reminder lead times" />
          <Table testid="maintenance-schedules-table" columns={[
            { key: "equipment", label: "Equipment" },
            { key: "category", label: "Category" },
            { key: "location_name", label: "Location" },
            { key: "recurrence", label: "Every", render: (r) => `${r.recurrence?.every || "—"} ${r.recurrence?.type || "days"}` },
            { key: "next_due_at", label: "Next due", render: (r) => fmtDate(r.next_due_at) },
            { key: "days_until_due", label: "Days", align: "right" },
            { key: "reminder_lead_days", label: "Lead", align: "right" },
          ]} rows={schedules} empty="No schedules configured" />
        </Card>
      )}

      {tab === "renewals" && (
        <Card testid="renewals-card">
          <SectionHead title="Renewal Reminders" sub="Licences, insurance, subscriptions and contracts" />
          <Table testid="renewals-table" columns={[
            { key: "service_name", label: "Service" },
            { key: "vendor", label: "Vendor" },
            { key: "location_name", label: "Location" },
            { key: "renewal_date", label: "Renewal", render: (r) => fmtDate(r.renewal_date) },
            { key: "days_until_due", label: "Days", align: "right" },
            { key: "amount", label: "Amount", align: "right", render: (r) => naira(r.amount) },
            { key: "status", label: "Status", render: (r) => <Pill status={r.status} /> },
          ]} rows={renewals} empty="No renewals tracked" />
        </Card>
      )}

      {form && (
        <Modal title={newBtn[0]} onClose={() => setForm(null)} onSave={save} testid="maintenance-modal">
          {form.kind !== "renewal" ? (
            <>
              <Field label="Equipment"><Input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} data-testid="maintenance-equipment-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
                <Field label="Location">
                  <Select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="maintenance-location-select">
                    <option value="">Select location</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Select>
                </Field>
              </div>
              {form.kind === "record" ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Issue" className="col-span-2"><Input value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} data-testid="maintenance-issue-input" /></Field>
                  <Field label="Priority">
                    <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                      {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
                    </Select>
                  </Field>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Recurrence">
                    <Select value={form.recurrence_type} onChange={(e) => setForm({ ...form, recurrence_type: e.target.value })}>
                      {["daily", "weekly", "monthly"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </Select>
                  </Field>
                  <Field label="Every"><Input type="number" value={form.every} onChange={(e) => setForm({ ...form, every: e.target.value })} /></Field>
                  <Field label="Next due"><Input type="date" value={form.next_due_at} onChange={(e) => setForm({ ...form, next_due_at: e.target.value })} data-testid="schedule-next-due-input" /></Field>
                  <Field label="Reminder lead days"><Input type="number" value={form.reminder_lead_days} onChange={(e) => setForm({ ...form, reminder_lead_days: e.target.value })} /></Field>
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="Service name"><Input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} data-testid="renewal-name-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></Field>
                <Field label="Location">
                  <Select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                    <option value="">Corporate</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Select>
                </Field>
                <Field label="Renewal date"><Input type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} data-testid="renewal-date-input" /></Field>
                <Field label="Amount (₦)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Reminder lead days"><Input type="number" value={form.reminder_lead_days} onChange={(e) => setForm({ ...form, reminder_lead_days: e.target.value })} /></Field>
              </div>
            </>
          )}
        </Modal>
      )}

      {resolve && (
        <Modal title={`Close: ${resolve.equipment}`} onClose={() => { setResolve(null); setNotes(""); }}
          onSave={doResolve} saveLabel="Close issue" testid="maintenance-resolve-modal">
          <Field label="Resolution notes (required)">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="maintenance-notes-input" />
          </Field>
        </Modal>
      )}
    </div>
  );
}
